import { parseSheetDate } from './dates';
import { LEASING, isInvygoNumber, periodKind, businessBayKindOf } from '../../../services/contractRules';
import logger from '../../../utils/logger';

/**
 * Pure row helpers of Monthly management (no React, no worker), so they can be tested alone.
 */

// --- HELPER: INVYGO CONTRACT ---
// Ejar numbers carry the branch code after the digits (D01 T1, D02 T2, D03
// Business Bay Branch, D04 Business Bay - Invygo). Anything D04 - or picked up
// at the Invygo branch - is an Invygo subscription car, whatever the booking
// number or duration says ("D04 المفروض اى حاجه فيها تبقى انفيجو").
// The branch code is read by the one shared branchCodeOf (DUP-04).
export const isInvygoContract = (row) => {
    if (isInvygoNumber(row?.contractNo || row?.['Contract No.'] || row?.contract_no)) return true;
    const branch = String(row?.pickupBranch || row?.['Pick-up Branch'] || row?.pickup_branch || '').toLowerCase();
    return branch.includes('invygo');
};

/**
 * Invygo customers pay Invygo, never Yelo (owner, 28 Sep 2026: "كل انفيجو مش بيدفعوا لينا فلوس"): on Ejar an Invygo
 * contract is a Broker one with no money at all (paid always 0, no vouchers), so its zero is not "Paid". Where Yelo
 * would call an Invygo contract or booking "Paid" it shows '—' with this note, and no Paid / Unpaid count takes it
 * (liveBalance invygoNoBalance / netBalanceExport / isUnpaid, bookingsService bookingPayment, the-engine
 * customerDue.logic payStatusOf / ejarPaidOf; the engine's booking_watch.payment_of and contract_statement say the same).
 */
export const INVYGO_NO_MONEY_NOTE = 'Invygo contract: paid to Invygo, no money on Ejar';

/**
 * A COMPANY contract (owner, 28 Sep 2026: Ejar lets no payment be added on a company contract - the accountant settles
 * it): Ejar's customer type "Corporate" when the row carries it, else Ejar's corporate contract number (C... / A...:
 * every C / A contract in Ejar is Corporate, and every Corporate one of the last years is C / A - read 1 Oct 2026).
 */
export const isCompanyContract = (row) => {
    // only a field holding Ejar's customer type counts (an old row's contract_type can hold the kind: 'Monthly' ...)
    const type = [row?.customerType, row?.['Customer Type'], row?.customer_type, row?.contract_type]
        .map((v) => String(v ?? '').trim().toLowerCase())
        .find((t) => t === 'corporate' || t === 'individual' || t === 'broker');
    if (type) return type === 'corporate';
    const no = String(row?.contractNo || row?.['Contract No.'] || row?.contract_no || '').trim().toUpperCase();
    return /^[CA]\d{4}/.test(no);                 // Ejar's shape: C2509000D03..., C2511ARE..., A2512000D01...
};
export const COMPANY_NO_MONEY_NOTE = 'Company contract: settled by the accountant, no payment on Ejar';

/**
 * Paid / remaining / to collect show '—' with this note, and no debt count takes the contract (owner, 1 Oct 2026:
 * "عايز يظهر - بس تكون كل حاجه متسجله"): an Invygo contract or a company contract. '' for any other contract. Its
 * charges keep showing as on any contract.
 */
export const noMoneyNoteOf = (row) => {
    if (isInvygoContract(row)) return INVYGO_NO_MONEY_NOTE;
    if (isCompanyContract(row)) return COMPANY_NO_MONEY_NOTE;
    return '';
};

/**
 * The category of a contract: 'Invygo' | 'Leasing' | 'Daily' | 'Monthly' (null when the rental period is
 * unknown). ONE rule for the Booking column, the contract-type filter, Fill from Ejar, the tabs, the fleet
 * and the Daily rental report (CON-10). Reads both the page rows (contractNo, bookingNumber, pickupDate …)
 * and the raw cached rows ('Contract No.', 'Booking Number' …).
 *   Invygo  - made from the Invygo branch: branch code D04 in the number, or the Invygo branch text. Never
 *             by the shape of the booking number (owner 26 Sep 2026: an all-digit booking is not Invygo)
 *   Leasing - booking starts with 'C' or says leasing, or no booking and the contract starts with 'C'
 *   Daily / Monthly - the booking says so, else the period: monthly from MONTHLY_MIN_DAYS (30) days
 */
export const contractCategory = (row) => {
    if (!row) return null;
    if (isInvygoContract(row)) return 'Invygo';
    const booking = String(row.bookingNumber ?? row['Booking Number'] ?? row.booking_number ?? '').trim();
    const bookingLower = booking.toLowerCase();
    const contractNo = String(row.contractNo ?? row['Contract No.'] ?? row.contract_no ?? '').trim().toLowerCase();
    if (bookingLower.startsWith('c') || bookingLower.includes('leasing') || (!booking && contractNo.startsWith('c'))) return LEASING;
    if (bookingLower.includes('daily')) return 'Daily';
    if (bookingLower.includes('monthly')) return 'Monthly';
    // a Business Bay contract: the engine's own Daily / Monthly (contractRules.businessBayKindOf); else the period
    const bayKind = businessBayKindOf(row);
    if (bayKind) return bayKind;
    const pickup = row.pickupDate ?? row['Pick-up Date'] ?? row.pickup_date;
    const dropoff = row.dropoffDate ?? row['Drop-off Date'] ?? row.dropoff_date;
    const start = pickup ? parseSheetDate(String(pickup)) : null;
    const end = dropoff ? parseSheetDate(String(dropoff)) : null;
    return periodKind(start, end);
};

// --- HELPER: DETERMINE CONTRACT TYPE ---
// The contract-type filter / Fill from Ejar / Excel: the category, else the stored type, else 'Monthly'.
export const determineContractType = (row) => contractCategory(row) || row.contractType || 'Monthly';

/** The contract-type filter of the page ('all' | 'leasing' | 'invygo' | 'daily' | 'monthly'). */
export const matchesContractType = (row, filter) => {
    if (!filter || filter === 'all') return true;
    const derivedType = determineContractType(row).toLowerCase();
    if (['leasing', 'invygo', 'daily', 'monthly'].includes(filter)) return derivedType === filter;
    return true;
};

/** An Excel cell: 0 stays 0 (a zero balance / paid used to export as an empty cell - CON-16); only a missing value is blank. */
export const exportCell = (val) => (val === undefined || val === null || (typeof val === 'number' && Number.isNaN(val)) ? '' : val);

/**
 * The Excel's 'Salik Count' of a month row: the trips the Fill from Ejar counted from the Ejar charge lines
 * (salik_count, 0 when none). It used to be guessed from the amount (/ 5.25, / 5, / 4), wrong with the
 * gate + 1.05 AED tariff (4 trips = 20.20 AED exported '1+'). A row never filled from Ejar with a Salik
 * amount has no count: blank, not a guess (CON-16).
 */
export const salikCountOf = (calc) => {
    if (calc && calc.salik_count !== undefined && calc.salik_count !== null && calc.salik_count !== '') return Number(calc.salik_count) || 0;
    if (calc && Array.isArray(calc.salik_list)) return calc.salik_list.length;
    return calc && parseFloat(calc.salik) > 0 ? '' : 0;
};

/** Search rows -> the two tables shown while searching (open / everything else). */
export const splitSearchResults = (rows) => ({
    openContractsFromSearch: (rows || []).filter(c => c.Status === 'Open'),
    closedContractsFromSearch: (rows || []).filter(c => c.Status !== 'Open')
});

/**
 * `readRow(id)` (the server copy) mapped by `toPageRow`, over the page row. A failed read keeps the
 * page copy: the calculator Save's version check still refuses a stale write (CON-03).
 */
export const freshRowOver = async (pageRow, readRow, toPageRow) => {
    if (!pageRow || !pageRow.id) return pageRow;
    try {
        const fresh = await readRow(pageRow.id);
        if (!fresh) return pageRow;
        return { ...pageRow, ...toPageRow(fresh) };
    } catch (e) {
        logger.warn('[contracts] could not re-read the contract; the calculator opens from the page copy', e);
        return pageRow;
    }
};
